import React, {ChangeEvent, DragEvent, useEffect, useRef, useState} from "react";
import {
    Box, Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    makeStyles,
    Paper,
    Switch,
    Typography
} from "@material-ui/core";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DescriptionIcon from "@material-ui/icons/Description";
import ImageIcon from "@material-ui/icons/Image";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ReplayIcon from "@material-ui/icons/Replay";
import {useNotify, useRefresh} from "react-admin";
import {clientNoJson, endpoint} from "../../dataProvider";
import {UTIF} from "../../Distribution/Note/UTIF_MOD";
import {usePanzoom} from "../../shared/hooks/usePanzoom";
import {NoteConvertDeliveryForm} from "./NoteConvertDeliveryForm";

const ACCEPTED_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "tif", "tiff", "webp"];
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const fileExtension = (name: string) => name.split(".").pop()?.toLowerCase() || "";
const isPdf = (name: string) => fileExtension(name) === "pdf";
const isTiff = (name: string) => ["tif", "tiff"].includes(fileExtension(name));

const useStyles = makeStyles((theme) => ({
    setupCard: {marginBottom: theme.spacing(2)},
    sectionTitle: {fontWeight: 700},
    dropzone: {
        boxSizing: "border-box",
        width: "100%",
        minHeight: 180,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: `2px dashed ${theme.palette.primary.main}`,
        borderRadius: 12,
        background: "#f7fbff",
        padding: theme.spacing(3, 2),
        textAlign: "center",
        cursor: "pointer",
        transition: "all .15s ease",
        "&:hover": {background: "#edf6ff"}
    },
    dropzoneActive: {background: "#e3f2fd", transform: "translateY(-1px)"},
    hiddenInput: {display: "none"},
    status: {fontWeight: 600},
    preview: {width: "100%", height: "75vh", border: 0, objectFit: "contain", background: "#f4f4f4"},
    inlinePreview: {width: "100%", height: "100%", border: 0, objectFit: "contain", background: "#f4f4f4"},
    inlinePreviewContainer: {
        height: 800,
        [theme.breakpoints.down("sm")]: {height: "75vh", minHeight: 520},
    },
    viewer: {border: `1px solid ${theme.palette.divider}`, borderRadius: 10, overflow: "hidden"},
    viewerHeader: {background: "#fafafa"},
    dataLabel: {display: "block", fontSize: 12, color: theme.palette.text.secondary},
    dataValue: {fontWeight: 600, minHeight: 24},
    draftFile: {
        display: "flex",
        alignItems: "center",
        padding: theme.spacing(1),
        borderTop: `1px solid ${theme.palette.divider}`
    },
}));

export const ConvertNoteDraftFiles = ({files, onAdd, onRemove, disabled, autoStart, onAutoStartChange}: {
    files: File[];
    onAdd: (files: FileList | File[]) => void;
    onRemove: (index: number) => void;
    disabled?: boolean;
    autoStart: boolean;
    onAutoStartChange: (checked: boolean) => void;
}) => {
    const classes = useStyles();
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const selectFiles = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.files) onAdd(event.target.files);
        event.target.value = "";
    };
    const dropFiles = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setDragging(false);
        if (!disabled) onAdd(event.dataTransfer.files);
    };

    return <div>
        <Box p={2}>
            <FormControlLabel label="Avvia automaticamente"
                              control={<Switch color="primary" checked={autoStart} disabled={disabled}
                                               onChange={(_, checked) => onAutoStartChange(checked)}/>}/>
            <input ref={inputRef} className={classes.hiddenInput} type="file" multiple disabled={disabled}
                   accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.webp" onChange={selectFiles}/>
            <Box mt={2} role="button" tabIndex={disabled ? -1 : 0}
                 className={`${classes.dropzone} ${dragging ? classes.dropzoneActive : ""}`}
                 onClick={() => !disabled && inputRef.current?.click()}
                 onKeyDown={(event) => {
                     if (!disabled && (event.key === "Enter" || event.key === " ")) inputRef.current?.click();
                 }}
                 onDragOver={(event) => {
                     event.preventDefault();
                     if (!disabled) setDragging(true);
                 }} onDragLeave={() => setDragging(false)} onDrop={dropFiles}>
                <CloudUploadIcon color="primary" style={{fontSize: 42}}/>
                <Typography variant="subtitle1">Trascina qui le bolle oppure scegli i file</Typography>
                <Typography variant="caption" color="textSecondary">PDF, JPG, PNG, TIFF o WEBP · massimo 20 MB per
                    file</Typography>
            </Box>
        </Box>
        {files.map((file, index) => <Box key={`${file.name}-${file.lastModified}-${index}`}
                                         className={classes.draftFile}>
            {isPdf(file.name) ? <DescriptionIcon color="action"/> : <ImageIcon color="action"/>}
            <Box ml={1} flexGrow={1} minWidth={0}>
                <Typography noWrap>{file.name}</Typography>
                <Typography variant="caption" color="textSecondary">{(file.size / 1024 / 1024).toFixed(1)} MB · pronto
                    per il salvataggio</Typography>
            </Box>
            <IconButton aria-label={`Rimuovi ${file.name}`} onClick={() => onRemove(index)}
                        disabled={disabled}><DeleteOutlineIcon/></IconButton>
        </Box>)}
    </div>;
};


export const ConvertNoteFilesWorkspace = ({
                                              convertNote,
                                              showUpload = true,
                                              onRefresh,
                                              split = false,
                                              hideNavigation = false
                                          }: {
    convertNote: any;
    showUpload?: boolean;
    onRefresh?: () => void;
    split?: boolean;
    hideNavigation?: boolean;
}) => {
    const classes = useStyles();
    const notify = useNotify();
    const globalRefresh = useRefresh();
    const refresh = onRefresh || globalRefresh;
    const [preview, setPreview] = useState<{ url: string; pdf: boolean; name: string } | null>(null);
    const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
    const [documentUrls, setDocumentUrls] = useState<{ [key: number]: string }>({});
    const [creatingId, setCreatingId] = useState<number | null>(null);
    const [creatingReceiverId, setCreatingReceiverId] = useState<number | null>(null);
    const [starting, setStarting] = useState(false);
    const handlePanzoom = usePanzoom({requireShift: false});


    const loadDocument = async (item: any) => {
        if (documentUrls[item.id]) return documentUrls[item.id];
        try {
            const response = await clientNoJson(`${endpoint}/note_convert/${convertNote.id}/files/${item.id}/download/`, {
                method: "GET",
                responseType: "arraybuffer"
            });
            let documentUrl: string;
            if (isTiff(item.file_name)) {
                // I browser non rendono TIFF direttamente: stessa conversione usata da ImageViewer.
                const ifds = UTIF.decode(response.data);
                UTIF.decodeImage(response.data, ifds[0], ifds);
                const rgba = UTIF.toRGBA8(ifds[0]);
                const canvas = document.createElement("canvas");
                canvas.width = ifds[0].width;
                canvas.height = ifds[0].height;
                const context = canvas.getContext("2d");
                if (!context) throw new Error("Canvas non disponibile");
                context.putImageData(new ImageData(new Uint8ClampedArray(rgba.buffer), ifds[0].width, ifds[0].height), 0, 0);
                documentUrl = canvas.toDataURL("image/png");
            } else {
                const fallbackType = isPdf(item.file_name) ? "application/pdf" : `image/${fileExtension(item.file_name)}`;
                documentUrl = window.URL.createObjectURL(new Blob([response.data], {type: response.headers["content-type"] || fallbackType}));
            }
            setDocumentUrls((current) => ({...current, [item.id]: documentUrl}));
            return documentUrl;
        } catch {
            notify("Impossibile aprire la bolla", "error");
            return null;
        }
    };
    const openPreview = async (item: any) => {
        const documentUrl = await loadDocument(item);
        if (documentUrl) setPreview({url: documentUrl, pdf: isPdf(item.file_name), name: item.file_name});
    };
    const closePreview = () => {
        setPreview(null);
    };
    const remove = async (item: any) => {
        if (!window.confirm(`Rimuovere “${item.file_name}”?`)) return;
        try {
            await clientNoJson(`${endpoint}/note_convert/${convertNote.id}/files/${item.id}/`, {method: "DELETE"});
            notify("Bolla rimossa", "info");
            refresh();
        } catch {
            notify("Impossibile rimuovere la bolla", "error");
        }
    };
    const statusLabel: { [key: string]: string } = {
        CR: "In attesa",
        IP: "In elaborazione",
        CO: "Elaborata",
        ER: "Errore"
    };
    const priority: { [key: string]: number } = {CO: 0, IP: 1, CR: 2, ER: 3};
    const files = [...(convertNote.files || [])].filter((item: any) => item.status !== "CV").sort((first: any, second: any) => (priority[first.status] ?? 9) - (priority[second.status] ?? 9));
    const selectedIndex = files.findIndex((item: any) => String(item.id) === String(selectedFileId));
    const selectedFile = selectedIndex >= 0 ? files[selectedIndex] : undefined;
    const fileIds = files.map((item: any) => item.id).join(",");
    useEffect(() => {
        if (!files.length) {
            if (selectedFileId !== null) setSelectedFileId(null);
            return;
        }
        // Mantiene la bolla aperta durante i refresh (ad esempio dopo aver
        // creato il destinatario). Se è stata rimossa/converita, seleziona la prima disponibile.
        if (!selectedFile) {
            setSelectedFileId(files[0].id);
            return;
        }
        loadDocument(selectedFile);
    }, [convertNote.id, fileIds, selectedFileId]);
    const saveDeliveryDraft = async (item: any, data: any) => {
        setCreatingId(item.id);
        try {
            await clientNoJson(`${endpoint}/note_convert/${convertNote.id}/files/${item.id}/`, {
                method: "PATCH", data: {delivery_data: data, receiver_data: data.receiver_data}
            });
            notify("Dati salvati e missioni verificate", "info");
            refresh();
        } catch (error: any) {
            notify("Impossibile salvare la bozza missione", "error");
            return error?.response?.data?.delivery_data || error?.response?.data;
        } finally {
            setCreatingId(null);
        }
    };
    const createDelivery = async (item: any, data: any = {}) => {
        const nextFile = files[files.findIndex((file: any) => file.id === item.id) + 1];
        setCreatingId(item.id);
        try {
            await clientNoJson(`${endpoint}/note_convert/${convertNote.id}/files/${item.id}/create-delivery/`, {
                method: "POST",
                data
            });
            notify("Delivery creata", "info");
            setSelectedFileId(nextFile ? nextFile.id : null);
            refresh();
        } catch (error: any) {
            const detail = error?.response?.data?.detail || Object.values(error?.response?.data || {}).flat().join(" ");
            notify(detail || "Impossibile creare la delivery", "error");
            return error?.response?.data;
        } finally {
            setCreatingId(null);
        }
    };
    const associateDelivery = async (item: any, deliveryId: number) => {
        const nextFile = files[files.findIndex((file: any) => file.id === item.id) + 1];
        setCreatingId(item.id);
        try {
            await clientNoJson(`${endpoint}/note_convert/${convertNote.id}/files/${item.id}/associate-delivery/`, {
                method: "POST",
                data: {delivery: deliveryId}
            });
            notify("Bolla associata alla missione", "info");
            setSelectedFileId(nextFile ? nextFile.id : null);
            refresh();
        } catch (error: any) {
            notify(error?.response?.data?.detail || "Impossibile associare la bolla alla missione", "error");
        } finally {
            setCreatingId(null);
        }
    };
    const createReceiver = async (item: any, receiverData: any) => {
        setCreatingReceiverId(item.id);
        try {
            const response = await clientNoJson(`${endpoint}/note_convert/${convertNote.id}/files/${item.id}/create-receiver/`, {
                method: "POST",
                data: {receiver_data: receiverData}
            });
            notify("Destinatario creato e associato alla bolla", "info");
            refresh();
            return response.data;
        } catch (error: any) {
            const errors = error?.response?.data?.receiver_data;
            const detail = (Array.isArray(errors) ? errors.join(" ") : errors ? Object.values(errors).flat().join(" ") : "") || error?.response?.data?.detail;
            notify(detail || "Impossibile creare il destinatario", "error");
        } finally {
            setCreatingReceiverId(null);
        }
    };
    const startProcessing = async () => {
        if (starting || ["Q", "IP"].includes(convertNote.status)) return;
        setStarting(true);
        try {
            const response = await clientNoJson(`${endpoint}/note_convert/${convertNote.id}/start/`, {method: "POST"});
            notify(response.data.status === "Q" ? "Elaborazione messa in coda" : "Elaborazione avviata", "info");
            refresh();
        } catch (error: any) {
            notify(error?.response?.data?.detail || "Impossibile avviare l'elaborazione", "error");
        } finally {
            setStarting(false);
        }
    };
    return <>
        <Paper elevation={0} className={classes.setupCard}>
            {selectedFile && <Box px={2} pb={2}>
                <Paper variant="outlined" className={classes.viewer}>
                    <Grid container spacing={0}>
                        <Grid item xs={12} md={split ? 4 : 12}>
                            <Box p={2}>
                                <Box p={2} className={classes.viewerHeader} display="flex" alignItems="center"
                                     justifyContent="space-between">
                                    <Box display="flex" alignItems="center">{isPdf(selectedFile.file_name) ?
                                        <DescriptionIcon color="action"/> : <ImageIcon color="action"/>}<Box
                                        ml={1}><Typography>{selectedFile.file_name}</Typography>{!hideNavigation &&
                                        <Typography variant="caption"
                                                    color="textSecondary">Bolla {selectedIndex + 1} di {files.length}</Typography>}
                                    </Box></Box>
                                    <Box display="flex" alignItems="center" flexShrink={0}>
                                        <Chip size="small" className={classes.status}
                                              color={selectedFile.status === "CO" ? "primary" : selectedFile.status === "ER" ? "secondary" : "default"}
                                              label={statusLabel[selectedFile.status] || selectedFile.status}/>
                                        {selectedFile.status === "ER" && <Box ml={1}
                                                                              title={["Q", "IP"].includes(convertNote.status)
                                                                                  ? "Attendi il termine dell'elaborazione prima di riprovare"
                                                                                  : "Rielabora le bolle in errore senza modificare quelle già completate"}>
                                            <Button size="small" variant="outlined" color="primary"
                                                    startIcon={starting ? <CircularProgress size={14}/> : <ReplayIcon/>}
                                                    disabled={starting || ["Q", "IP"].includes(convertNote.status)}
                                                    onClick={startProcessing}>
                                                {starting ? "Riavvio…" : "Riprova"}
                                            </Button>
                                        </Box>}
                                    </Box>
                                </Box>
                                {documentUrls[selectedFile.id] ?
                                    <Box className={classes.inlinePreviewContainer} overflow="hidden"
                                         style={{background: "#f4f4f4", touchAction: "none"}}
                                         title="Rotellina: zoom · trascina: sposta · doppio clic: reset">
                                        <div ref={handlePanzoom}
                                             style={{height: "100%", width: "100%"}}>{isPdf(selectedFile.file_name) ?
                                            <iframe className={classes.inlinePreview}
                                                    src={documentUrls[selectedFile.id]}
                                                    title={selectedFile.file_name}/> :
                                            <img className={classes.inlinePreview} src={documentUrls[selectedFile.id]}
                                                 alt={selectedFile.file_name}/>}</div>
                                    </Box> :
                                    <Box className={classes.inlinePreviewContainer} display="flex" alignItems="center"
                                         justifyContent="center"><CircularProgress/></Box>}
                                <Box mt={1} display="flex" justifyContent="space-between">
                                    <Button size="small" startIcon={<VisibilityIcon/>}
                                            onClick={() => openPreview(selectedFile)}>Apri a schermo intero</Button>
                                    <Button size="small" color="secondary" startIcon={<DeleteOutlineIcon/>}
                                            onClick={() => remove(selectedFile)}>Rimuovi</Button>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={split ? 8 : 12}>
                            {selectedFile.status === "CO" ? <NoteConvertDeliveryForm key={selectedFile.id}
                                                                                     file={selectedFile}
                                                                                     convertNote={convertNote}
                                                                                     busy={creatingId !== null || creatingReceiverId !== null}
                                                                                     saveDraft={(data) => saveDeliveryDraft(selectedFile, data)}
                                                                                     createDelivery={(data) => createDelivery(selectedFile, data)}
                                                                                     createReceiver={(data) => createReceiver(selectedFile, data)}
                                                                                     associateDelivery={(id) => associateDelivery(selectedFile, id)}
                                                                                     remove={() => remove(selectedFile)}/> :
                                <Box p={3}><Typography color="textSecondary">I dati della missione saranno disponibili
                                    al termine dell’elaborazione.</Typography></Box>}
                        </Grid>
                    </Grid>
                </Paper>
            </Box>}
        </Paper>
        <Dialog open={Boolean(preview)} onClose={closePreview} fullWidth
                maxWidth="xl"><DialogTitle>{preview?.name}</DialogTitle><DialogContent dividers
                                                                                       style={{padding: 0}}>{preview?.pdf ?
            <iframe className={classes.preview} src={preview.url} title={preview.name}/> :
            <img className={classes.preview} src={preview?.url} alt={preview?.name}/>}</DialogContent></Dialog>
    </>;
};